/**
 * Analytical Tree - Main Tab Component
 * Container with controls, graph/table view, and node details panel.
 */

import React, { useState, useCallback } from 'react';
import { Box, Grid, Typography, Alert, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import axios from 'axios';
import ControlsPanel from './ControlsPanel';
import GraphView from './GraphView';
import NodeDetailsPanel from './NodeDetailsPanel';
import NodeTable from './NodeTable';

const AnalyticalTreeTab = () => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' | 'table'

  const handleLoad = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    try {
      const token = localStorage.getItem('token');
      const searchParams = new URLSearchParams({
        rootType: params.rootType,
        rootId: params.rootId,
        maxDepth: String(params.maxDepth ?? 3)
      });
      if (params.asOf) searchParams.set('asOf', params.asOf);
      if (params.includeTypes?.length) searchParams.set('includeTypes', params.includeTypes.join(','));
      if (params.onlyWithAnomalies) searchParams.set('onlyWithAnomalies', 'true');

      const res = await axios.get(`/api/analytics-tree?${searchParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNodes(res.data.nodes || []);
      setEdges(res.data.edges || []);
      setMeta(res.data.meta || null);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load tree');
      setNodes([]);
      setEdges([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Analytical Tree
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only graph view of entity relationships. Select a root and load to visualize data and detect anomalies.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, v) => {
            if (v) setViewMode(v);
          }}
        >
          <ToggleButton value="graph">Graph</ToggleButton>
          <ToggleButton value="table">Table</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ControlsPanel onLoad={handleLoad} loading={loading} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {meta && (
        <Paper sx={{ p: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            As of: {meta.asOf} | Nodes: {nodes.length} | Edges: {edges.length}
            {meta.anomalyCounts && (
              <> | Anomalies: {meta.anomalyCounts.critical ?? 0} critical, {meta.anomalyCounts.warning ?? 0} warning</>
            )}
          </Typography>
        </Paper>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          {viewMode === 'graph' ? (
            <GraphView
              nodes={nodes}
              edges={edges}
              onSelectNode={setSelectedNode}
            />
          ) : (
            <NodeTable
              nodes={nodes}
              onSelectNode={setSelectedNode}
            />
          )}
        </Grid>
        <Grid item xs={12} md={3}>
          <NodeDetailsPanel node={selectedNode} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticalTreeTab;
